import React, { ReactNode } from 'react'

import { ThemeProvider } from '@mui/material'
import Dayjs from 'dayjs'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import DatePicker from 'components_new/atoms/DatePicker'
import InputAdornment from 'components_new/atoms/InputAdornment'
import Link from 'components_new/atoms/Link'
import Skeleton from 'components_new/atoms/Skeleton'
import TextField from 'components_new/atoms/TextField'

import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'
import RichTextEditor from 'components_new/molecules/RichTextEditor'
import { toEditorValue } from 'components_new/molecules/RichTextEditor/contract'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { getTheme } from 'themes'

import type { TRichText } from '@homepal/api-contract'

import { TInputType, createChangeEvent, isResetable } from './utils'

interface SettingsEditDialogProps {
  clearValue?: string
  disabled?: boolean

  /**
   * The field's own helper text. Separate from the computed `helperText`
   * below, which is spoken for by the Rensa link.
   */
  fieldHelperText?: ReactNode
  loading?: boolean

  /** `text` only: a paragraph rather than a line. */
  multiline?: boolean
  rows?: number

  /** `multiselect` only — `value` stays the single-value channel. */
  multiValue?: string[]
  onMultiChange?: (value: string[]) => void

  /** `rich_text` only, for the same reason. */
  richValue?: TRichText | null
  onRichChange?: (value: TRichText | null) => void
  maxDate?: string
  maxNumber?: number
  onChange: (() => void) | ((value: string) => void)
  onClose: () => void
  onSubmit: () => void
  open: boolean
  options?: Option[]
  required?: boolean
  title: string
  type: TInputType
  unit?: string | null
  value: string
  variant?: 'add' | 'edit'
}

const SettingsEditDialog = (props: SettingsEditDialogProps) => {
  const {
    clearValue = '',
    disabled,
    loading = false,
    maxDate,
    maxNumber,
    onChange,
    onClose,
    onSubmit,
    open,
    options,
    required,
    title,
    type,
    unit,
    fieldHelperText,
    multiline,
    rows,
    multiValue,
    onMultiChange,
    richValue,
    onRichChange,
    value,
    variant
  } = props

  const { t } = useTranslation()

  const handleSubmit = () => {
    onSubmit()
    onClose()
  }

  const handleReset = () => {
    const event = createChangeEvent(clearValue)

    onChange(event.target.value)
  }

  // Helper text for when we implement catch Enter in full dialog:
  /* 
    <span>
      {`Tips! Tryck Enter ⏎ för att ${
        variant === 'add' ? 'lägga till' : 'spara'
      }.`}
    </span> 
  */

  // The field's own helper sits to the left of the Rensa link, so a field
  // can explain itself without giving up the reset.
  const helperText
    = fieldHelperText || isResetable(type, options) ? (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: fieldHelperText ? 'space-between' : 'flex-end'
        }}
      >
        {fieldHelperText ? <span>{fieldHelperText}</span> : null}
        {isResetable(type, options) ? (
          <Link disabled={value === ''} onClick={handleReset}>
            {t('global', 'clear')}
          </Link>
        ) : null}
      </Box>
    ) : undefined

  const renderInput = () => {
    if (loading) {
      return <Skeleton height={54} />
    }

    if (type === 'year_month') {
      return (
        <DatePicker
          label={'År och månad'}
          maxDate={maxDate}
          onChange={(event) => onChange(Dayjs(event).format('YYYY-MM'))}
          popperSx={{ zIndex: 1303 }}
          views={['year', 'month']}
          value={value}
          size={'medium'}
          sx={{ width: '100%', mt: 1 }}
        />
      )
    } else if (type === 'select') {
      return (
        <SelectSearch
          closeOnSelect
          multiple={false}
          noGutter={true}
          onChange={(option) => {
            if (!option || option.id === 'none') {
              handleReset()
            } else {
              onChange(option.id as string)
            }
          }}
          options={options || []}
          selected={options?.find((o) => o.id === value) || null}
          size={'medium'}
          sx={{ mt: 1 }}
        />
      )
    }

    if (type === 'multiselect') {
      return (
        <SelectSearch
          multiple={true}
          noGutter={true}
          onChange={(selected) =>
            onMultiChange?.(selected.map((option) => option.id as string))
          }
          options={options || []}
          selected={(options || []).filter((option) =>
            (multiValue || []).includes(option.id as string)
          )}
          size={'medium'}
          sort={true}
          sx={{ mt: 1 }}
        />
      )
    } else if (type === 'rich_text') {
      return (
        <RichTextEditor
          autoFocus={true}
          edit={true}
          helperText={
            typeof fieldHelperText === 'string' ? fieldHelperText : undefined
          }
          onChange={(next) => onRichChange?.(next)}
          settings={{
            bold: true,
            italic: true,
            underline: true,
            bulletedList: true,
            numberedList: true,
            code: true
          }}
          sx={{ mt: 1 }}
          value={toEditorValue(richValue ?? null)}
          variant="outlined"
        />
      )
    }

    return (
      <TextField
        autoFocus={true}
        helperText={helperText}
        multiline={multiline}
        rows={multiline ? (rows ?? 4) : undefined}
        InputProps={{
          endAdornment: Boolean(unit) ? (
            <InputAdornment position="end">{unit}</InputAdornment>
          ) : null
        }}
        inputProps={
          type === 'decimal'
            ? {
                inputMode: 'numeric',
                pattern: '[0-9]+([,][0-9]+)?',
                step: 'any'
              }
            : type === 'number'
              ? {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  max: maxNumber,
                  min: 0
                }
              : undefined
        }
        label={title}
        onChange={(e) => {
          const newValue = e.target.value

          if (type === 'number' && maxNumber) {
            // Ensure max constraint
            const num = Number(newValue)

            if (!isNaN(num)) {
              // clamp to max number
              onChange(Math.min(num, maxNumber).toString())

              return
            }
          }

          onChange(newValue)
        }}
        onKeyDown={(event) => {
          // In a paragraph Enter belongs to the text.
          if (event.key === 'Enter' && !multiline) {
            handleSubmit()
          }
        }}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        type={
          type === 'decimal' || type === 'number'
            ? 'number'
            : type === 'date'
              ? 'date'
              : undefined
        }
        value={value}
      />
    )
  }

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          <>
            <Button onClick={onClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={
                disabled || ((variant === 'add' || required) && value === '')
              }
              onClick={handleSubmit}
            >
              {variant === 'add' ? t('global', 'add') : t('global', 'save')}
            </Button>
          </>
        }
        disableRestoreFocus={true}
        onClose={onClose}
        open={open}
        sx={{
          zIndex: 1302 // for fullSize
        }}
        title={`${
          variant === 'add' ? t('global', 'add') : t('global', 'edit')
        } ${title.toLowerCase()}`}
      >
        {renderInput()}
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default SettingsEditDialog
